import React, {Fragment} from "react";
import PropTypes from 'prop-types'
import {Redirect, Route} from "react-router-dom";
import {CSSTransition, Transition, TransitionGroup} from "react-transition-group";
import {nearestClass, testPath} from "../lib/tools";
import {RouteContext} from "../contexts";

//<editor-fold desc="Transition helpers">
const unmountMiddleman = (element) => {
	let parent = element.parentElement;
	if (parent.classList.contains("animationMiddleman")) {
		element.style.width = "";
		element.style.height = "";
		element.style.margin = "";
		parent.parentElement.insertBefore(element, parent);
		parent.parentElement.removeChild(parent);
	}
};

const mountMiddleman = (element, middleman) => {
	unmountMiddleman(element);
	element.parentElement.insertBefore(middleman, element);
	middleman.appendChild(element);
};

const createMiddleman = (element = null, enter = false, width = false, height = false) => {
	let middleman = document.createElement("div");
	middleman.className = "animationMiddleman " + (enter ? "enter" : "exit");
	middleman.style.margin = "0";
	middleman.style.flexShrink = "0";
	
	if (element) {
		const w = element.offsetWidth;
		const h = element.offsetHeight;
		middleman.style.maxWidth = w + "px";
		middleman.style.maxHeight = h + "px";
		middleman.style.width = width ? 0 : w + "px";
		middleman.style.height = height ? 0 : h + "px";
		element.style.margin = 0;
		element.style.width = w + "px";
		element.style.height = h + "px";
	}
	return middleman;
};

const generateInjectionAnimation = (width = false, height = false) => ({
	onEnter: e => {
		unmountMiddleman(e);
		mountMiddleman(e, createMiddleman(e, true, width, height));
	},
	onEntering: e => {
		let middle = e.parentElement;
		middle.style.margin = "";
		middle.style.width = middle.style.maxWidth;
		middle.style.height = middle.style.maxHeight;
	},
	onEntered: e => {
		unmountMiddleman(e);
	},
	onExit: e => {
		unmountMiddleman(e);
		mountMiddleman(e, createMiddleman(e));
	},
	onExiting: e => {
		let middle = e.parentElement;
		if (width)
			middle.style.width = "0";
		if (height)
			middle.style.height = "0";
		middle.style.margin = "0";
	},
	onExited: e => {
		unmountMiddleman(e);
	}
});
//</editor-fold>

const transitions = {
	swap: {
		popIn: {
			timeout: 500,
			mountOnEnter: true,
			unmountOnExit: true,
		},
		slideX: {
			timeout: 500,
			mountOnEnter: true,
			unmountOnExit: true
		}
	},
	inject: {
		expand: {
			timeout: 500,
			mountOnEnter: true,
			unmountOnExit: true,
			...generateInjectionAnimation(true, true)
		},
		expandY: {
			timeout: 500,
			mountOnEnter: true,
			unmountOnExit: true,
			...generateInjectionAnimation(false, true)
		},
		expandX: {
			timeout: 500,
			mountOnEnter: true,
			unmountOnExit: true,
			...generateInjectionAnimation(true, false)
		},
		tableSlide: {
			timeout: 500,
			mountOnEnter: true,
			unmountOnExit: true,
			...generateInjectionAnimation(true, false)
		},
		section: {
			timeout: 1300,
			mountOnEnter: true,
			unmountOnExit: true,
			...generateInjectionAnimation(false, true)
		},
		delayFade: {
			timeout: 1500,
			mountOnEnter: true,
			unmountOnExit: true,
			...generateInjectionAnimation(false, true)
		}
	},
	appear: {
		growY: {
			timeout: 500,
			mountOnEnter: true,
			unmountOnExit: true
		},
		growX: {
			timeout: 500,
			mountOnEnter: true,
			unmountOnExit: true
		},
		growCenter: {
			timeout: 500,
			mountOnEnter: true,
			unmountOnExit: true
		},
		genie: {
			timeout: 400,
			mountOnEnter: true,
			unmountOnExit: false
		}
	}
};

export class TransitionSwitch extends React.Component {
	
	static contextType = RouteContext;
	
	static propTypes = {
		children: PropTypes.node,
		component: PropTypes.func.isRequired,
		config: PropTypes.object,
		animation: PropTypes.string,
		className: PropTypes.string,
		invisible: PropTypes.bool
	};
	
	render() {
		const {children, component, config, animation, className, invisible, relative} = this.props;
		let newChildren = [];
		
		let match = null;
		React.Children.forEach(children, (child, index) => {
			if (!React.isValidElement(child)) return;
			if (child.type !== Route) {
				if (child.type === Redirect) {
					if (child.props.from && !testPath(child.props.from, {exact: true}))
						return;
				}
				newChildren.push(React.cloneElement(child, {key: index}));
				return;
			} else if (match)
				return;
			
			const {path, id, ...props} = child.props;
			const newRoute = relative && path ?
				"/" + (this.context.route + "/" + path.replace(/(?:^\/*|\/*$)/g, "")).replace(/^\/*/g, "") :
				this.context.route;
			
			match = testPath(relative ? newRoute : path, props);
			if (match) {
				const key = id ? id : (path ? path : "noPath");
				newChildren.push(React.cloneElement(child, {
					location: window.location,
					computedMatch: match,
					key
				}));
			}
		}, this);
		
		return (
			<RouteContext.Provider value={{route: match && relative ? match.path : this.context.route}}>
				{React.createElement(component, {
					...config,
					animation: animation,
					className: className,
					invisible: invisible
				}, newChildren)}
			</RouteContext.Provider>
		);
	}
}

export class SwapTransitionGroup extends React.Component {
	
	render() {
		const {children, animation, className, target, invisible, absolute} = this.props;
		let newTarget;
		
		if (target)
			newTarget = nearestClass(target, animation);
		
		const child = React.Children.toArray(children)[0] || null;
		
		if (child && child.props.hasOwnProperty("order")) {
			let order = child.props.order;
			if (this.order !== order) {
				this.reverse = order < (this.order || -1);
				this.order = order;
			}
		}
		
		const innards = (
			<TransitionGroup component={null}>
				<Fragment>
					<div className="animationTarget" style={{
						position: "absolute",
						left: newTarget ? newTarget.left : 0,
						top: newTarget ? newTarget.top : 0,
						width: newTarget ? newTarget.element.offsetWidth : 0,
						height: newTarget ? newTarget.element.offsetHeight : 0
					}}/>
				</Fragment>
				{child &&
				<CSSTransition
					classNames={animation}
					{...transitions.swap[animation]}
					key={(child && child.props.id) || "noKey"}>
					{child}
				</CSSTransition>}
			</TransitionGroup>
		);
		if (invisible)
			return innards;
		else
			return <div ref={r => this.container = r} style={{position: absolute ? "absolute" : "relative"}}
			            className={[className, animation, this.reverse ? "reverse" : ""].join(" ")}>{innards}</div>;
	}
}


export const AppearTransition = ({children, animation, show, ...props}) => (
	<CSSTransition
		in={show}
		classNames={animation}
		{...transitions.appear[animation]}
		{...props}>
		{React.Children.only(children)}
	</CSSTransition>
);

export const InjectTransitionGroup = ({children, animation, className, invisible}) => {
	const innards = (
		<TransitionGroup component={null}>
			{React.Children.map(children, x => x && (x.type === CSSTransition || x.type === Transition ? x :
					<CSSTransition
						key={x.props.id}
						classNames={animation}
						{...transitions.inject[animation]}>
						{x}
					</CSSTransition>
			))}
		</TransitionGroup>
	);
	
	if (invisible)
		return innards;
	else
		return <div className={[className, animation].join(" ")}>{innards}</div>;
	
	
};



// WEBPACK FOOTER //
// ./src/components/animations.js